import type { ArcadeConfigFormValues } from '~/hooks/useArcadeConfigForm';
import { GAME_CONSTANTS, STORAGE_KEYS } from '~/utils/constants';

import type { GameSessionData } from '~/types';

function getStorageItem<T>(
  key: string,
  storage: Storage = localStorage
): T | null {
  try {
    const item = storage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error(`Error reading from storage (key: ${key}):`, error);
    return null;
  }
}

function setStorageItem<T>(
  key: string,
  value: T,
  storage: Storage = localStorage
): void {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Error writing to storage (key: ${key}):`, error);
  }
}

/**
 * Safely remove an item from storage
 */
function removeStorageItem(key: string, storage: Storage = localStorage): void {
  try {
    storage.removeItem(key);
  } catch (error) {
    console.error(`Error removing from storage (key: ${key}):`, error);
  }
}

// ============================================================================
// Daily Mode
// ============================================================================

export interface GameState {
  remainingAttempts: number;
  similarGames: string[];
  isGuessed: boolean;
  sessionId: string | null;
  gameStartedAt: number | null;
  timeAdjustment: number;
}

const DEFAULT_GAME_STATE: GameState = {
  remainingAttempts: GAME_CONSTANTS.MAX_ATTEMPTS,
  similarGames: [],
  isGuessed: false,
  sessionId: null,
  gameStartedAt: null,
  timeAdjustment: 0
};

export function getGameState(): GameState {
  const state = getStorageItem<GameState>(
    STORAGE_KEYS.GAME_STATE,
    sessionStorage
  );
  return state || DEFAULT_GAME_STATE;
}

export function setGameState(state: GameState): void {
  setStorageItem(STORAGE_KEYS.GAME_STATE, state, sessionStorage);
}

export function updateGameState(updates: Partial<GameState>): GameState {
  const currentState = getGameState();
  const newState = { ...currentState, ...updates };
  setGameState(newState);
  return newState;
}

export function resetGameState(sessionId: string, offset = true): GameState {
  const newState: GameState = {
    remainingAttempts: GAME_CONSTANTS.MAX_ATTEMPTS,
    similarGames: [],
    isGuessed: false,
    sessionId,
    gameStartedAt:
      Date.now() +
      (offset
        ? GAME_CONSTANTS.COUNTDOWN_INITIAL_DELAY +
          GAME_CONSTANTS.COUNTDOWN_STEP_DURATION * 3
        : 0),
    timeAdjustment: 0
  };
  setGameState(newState);
  return newState;
}

export function clearGameState(): void {
  removeStorageItem(STORAGE_KEYS.GAME_STATE, sessionStorage);
}

// ============================================================================
// Arcade Mode
// ============================================================================

export function getArcadeGameSession(): GameSessionData | null {
  return getStorageItem<GameSessionData>(STORAGE_KEYS.ARCADE_GAME_SESSION);
}

export function setArcadeGameSession(session: GameSessionData): void {
  setStorageItem(STORAGE_KEYS.ARCADE_GAME_SESSION, session);
}

export function clearArcadeGameSession(): void {
  removeStorageItem(STORAGE_KEYS.ARCADE_GAME_SESSION);
}

export function getArcadeConfig(): ArcadeConfigFormValues | null {
  return getStorageItem<ArcadeConfigFormValues>(STORAGE_KEYS.ARCADE_CONFIG);
}

export function setArcadeConfig(config: ArcadeConfigFormValues): void {
  setStorageItem(STORAGE_KEYS.ARCADE_CONFIG, config);
}

export function clearArcadeConfig(): void {
  removeStorageItem(STORAGE_KEYS.ARCADE_CONFIG);
}

// ============================================================================
// Spotlight Mode
// ============================================================================

export function getSpotlightGameSession(): GameSessionData | null {
  return getStorageItem<GameSessionData>(STORAGE_KEYS.SPOTLIGHT_GAME_SESSION);
}

export function setSpotlightGameSession(session: GameSessionData): void {
  setStorageItem(STORAGE_KEYS.SPOTLIGHT_GAME_SESSION, session);
}

export function clearSpotlightGameSession(): void {
  removeStorageItem(STORAGE_KEYS.SPOTLIGHT_GAME_SESSION);
}

// ============================================================================
// Survival Mode
// ============================================================================

export function getSurvivalStartTime(sessionId: string): number | null {
  const key = STORAGE_KEYS.SURVIVAL_START_TIME(sessionId);
  const value = localStorage.getItem(key);
  return value ? parseInt(value, 10) : null;
}

export function setSurvivalStartTime(sessionId: string, time: number): void {
  const key = STORAGE_KEYS.SURVIVAL_START_TIME(sessionId);
  localStorage.setItem(key, time.toString());
}

export function getSurvivalGamesPlayed(sessionId: string): number | null {
  const key = STORAGE_KEYS.SURVIVAL_GAMES_PLAYED(sessionId);
  const value = localStorage.getItem(key);
  return value ? parseInt(value, 10) : null;
}

export function setSurvivalGamesPlayed(sessionId: string, count: number): void {
  const key = STORAGE_KEYS.SURVIVAL_GAMES_PLAYED(sessionId);
  localStorage.setItem(key, count.toString());
}

export function getSurvivalRiddlesSeen(sessionId: string): string[] {
  const key = STORAGE_KEYS.SURVIVAL_RIDDLES_SEEN(sessionId);
  const value = getStorageItem<string[]>(key);
  return value || [];
}

export function setSurvivalRiddlesSeen(
  sessionId: string,
  riddles: string[]
): void {
  const key = STORAGE_KEYS.SURVIVAL_RIDDLES_SEEN(sessionId);
  setStorageItem(key, riddles);
}

export function addSurvivalRiddleSeen(
  sessionId: string,
  riddleId: string
): void {
  const seen = getSurvivalRiddlesSeen(sessionId);
  if (!seen.includes(riddleId)) {
    seen.push(riddleId);
    setSurvivalRiddlesSeen(sessionId, seen);
  }
}

export function getSurvivalPartialMatchingThreshold(
  sessionId: string
): number | null {
  const key = STORAGE_KEYS.SURVIVAL_PARTIAL_MATCHING_THRESHOLD(sessionId);
  const value = localStorage.getItem(key);
  return value ? parseInt(value, 10) : null;
}

export function setSurvivalPartialMatchingThreshold(
  sessionId: string,
  threshold: number
): void {
  const key = STORAGE_KEYS.SURVIVAL_PARTIAL_MATCHING_THRESHOLD(sessionId);
  localStorage.setItem(key, threshold.toString());
}

// ============================================================================
// User / Anonymous
// ============================================================================

export function getAnonymousId(): string | null {
  return localStorage.getItem(STORAGE_KEYS.ANONYMOUS_ID);
}

export function setAnonymousId(id: string): void {
  localStorage.setItem(STORAGE_KEYS.ANONYMOUS_ID, id);
}
